<template>
    <div>
        <app-template>
            <!-- Vue d’ensemble -->
            <section>
                <h2 class="text-lg font-semibold mb-4">Vue d’ensemble</h2>

                <div v-if="loading" class="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div v-for="i in 3" :key="i" class="bg-white p-6 rounded-2xl border border-gray-100">
                        <div class="h-4 bg-gray-200 rounded w-1/2 mb-3"></div>
                        <div class="h-8 bg-gray-300 rounded w-1/3"></div>
                    </div>
                </div>

                <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <!-- Interventions du jour -->
                    <div class="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                        <p class="text-sm text-gray-500 mb-1">Interventions du jour</p>
                        <h3 class="text-2xl font-bold text-gray-800">
                            {{ dashboard.overview.today.count }}
                        </h3>
                        <p class="text-sm font-medium mt-1"
                           :class="dashboard.overview.today.delta >= 0 ? 'text-green-600' : 'text-red-600'">
                            {{ formatDelta(dashboard.overview.today.delta) }} depuis hier
                        </p>
                    </div>

                    <!-- Clients actifs -->
                    <div class="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                        <p class="text-sm text-gray-500 mb-1">Clients actifs (30j)</p>
                        <h3 class="text-2xl font-bold text-gray-800">
                            {{ dashboard.overview.clients.active_30_days }}
                        </h3>
                        <p class="text-sm font-medium mt-1"
                           :class="dashboard.overview.clients.delta >= 0 ? 'text-green-600' : 'text-red-600'">
                            {{ formatDelta(dashboard.overview.clients.delta) }} ce mois-ci
                        </p>
                    </div>

                    <!-- Chiffre d’affaires -->
                    <!-- <div class="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                        <p class="text-sm text-gray-500 mb-1">Factures émises</p>
                        <h3 class="text-2xl font-bold text-gray-800">
                            {{ formatCurrency(dashboard.overview.revenue.month) }}
                        </h3>
                        <p class="text-sm font-medium mt-1"
                           :class="dashboard.overview.revenue.delta_percent >= 0 ? 'text-green-600' : 'text-red-600'">
                            {{ dashboard.overview.revenue.delta_percent }}% vs dernier mois
                        </p>
                    </div> -->
                </div>
            </section>

            <!-- Interventions récentes -->
            <section class="mt-8">
                <h2 class="text-lg font-semibold mb-4">Interventions à venir</h2>

                <div v-if="loading" class="bg-white p-6 rounded-2xl border border-gray-100">
                    <div class="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
                    <div class="space-y-3">
                        <div v-for="i in 3" :key="i" class="h-4 bg-gray-200 rounded"></div>
                    </div>
                </div>

                <div v-else class="overflow-hidden bg-white rounded-2xl shadow-sm border border-gray-100">
                    <table class="w-full text-sm text-left">
                        <thead class="bg-gray-100 text-gray-600 uppercase text-xs">
                            <tr>
                                <th class="px-6 py-3">Date</th>
                                <th class="px-6 py-3">Client</th>
                                <th class="px-6 py-3">Type</th>
                                <th class="px-6 py-3">Statut</th>
                                <th class="px-6 py-3 text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-100">
                            <tr v-for="item in dashboard.recent_interventions"
                                :key="item.id">
                                <td class="px-6 py-3">{{ item.date || '-' }}</td>
                                <td class="px-6 py-3">{{ item.client }}</td>
                                <td class="px-6 py-3">{{ item.type }}</td>
                                <td class="px-6 py-3">
                                    <span class="px-3 py-1 rounded-full text-xs"
                                          :class="statusClass(item.status.color)">
                                        {{ item.status.label }}
                                    </span>
                                </td>
                                <td class="px-6 py-3 text-right">
                                    <button @click="$router.push(`/app/interventions?intervention_id=${item.id}`)" class="px-4 py-2 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700 transition-colors">
                                        Consulter
                                    </button>
                                </td>
                            </tr>

                            <tr v-if="dashboard.recent_interventions.length === 0">
                                <td colspan="5" class="px-6 py-6 text-center text-gray-400">
                                    Aucune intervention récente
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>
        </app-template>
    </div>
</template>

<script>
export default {
    name: "HomeDashboard",

    data() {
        return {
            loading: true,
            dashboard: {
                overview: {
                    today: { count: 0, delta: 0 },
                    clients: { active_30_days: 0, delta: 0 },
                    revenue: { month: 0, delta_percent: 0 }
                },
                recent_interventions: []
            },
        };
    },

    async mounted() {
        this.loadDashboard();
    },

    methods: {
        async loadDashboard() {
            try {
                const res = await this.$request.get('/home-dashboard');
                this.dashboard = res;
            } catch (e) {
                console.error('Erreur chargement dashboard', e);
            } finally {
                this.loading = false;
            }
        },

        formatDelta(value) {
            return value > 0 ? `+${value}` : value;
        },

        formatCurrency(value) {
            return new Intl.NumberFormat('fr-FR', {
                style: 'currency',
                currency: 'EUR'
            }).format(value || 0);
        },

        statusClass(color) {
            const map = {
                red: 'bg-red-100 text-red-700',
                green: 'bg-green-100 text-green-700',
                yellow: 'bg-yellow-100 text-yellow-700',
                blue: 'bg-blue-100 text-blue-700',
                gray: 'bg-gray-100 text-gray-600'
            };
            return map[color] || map.gray;
        }
    }
};
</script>
